
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from 'sonner';
import { nigerianStates, mockLGAs } from '@/data/nigerianStates';

interface MarketplaceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBusinessAdded: () => void;
}

const MarketplaceForm = ({ open, onOpenChange, onBusinessAdded }: MarketplaceFormProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    business_category: '',
    contact_phone: '',
    website_url: '',
    state: '',
    local_government: ''
  });

  const businessCategories = [
    'Technology & Software',
    'Healthcare & Medical',
    'Education & Training',
    'Food & Restaurants',
    'Retail & Fashion',
    'Construction & Real Estate',
    'Financial Services',
    'Transportation & Logistics',
    'Entertainment & Media',
    'Professional Services',
    'Agriculture & Farming',
    'Manufacturing',
    'Beauty & Wellness',
    'Tourism & Hospitality',
    'Other'
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Reset local government when state changes
    if (field === 'state') {
      setFormData(prev => ({ ...prev, local_government: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to add businesses');
      return;
    }

    // Validate required fields
    if (!formData.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Business description is required');
      return;
    }
    
    if (!formData.contact_phone.trim()) {
      toast.error('Contact phone is required');
      return;
    }
    
    if (!formData.state) {
      toast.error('State is required');
      return;
    }
    
    if (!formData.local_government) {
      toast.error('Local Government is required');
      return;
    }

    setLoading(true);
    
    try {
      // Prepare data - website_url is truly optional
      const businessData = {
        business_name: formData.business_name.trim(),
        description: formData.description.trim(),
        business_category: formData.business_category || null,
        contact_phone: formData.contact_phone.trim(),
        website_url: formData.website_url.trim() || null,
        state: formData.state,
        local_government: formData.local_government,
        approved: false
      };

      console.log('Submitting business data:', businessData);

      const { error } = await supabase
        .from('community_marketplace')
        .insert([businessData]);

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      toast.success('Business listing submitted successfully! It will be reviewed by an admin.');
      
      // Reset form
      setFormData({
        business_name: '',
        description: '',
        business_category: '',
        contact_phone: '',
        website_url: '',
        state: '',
        local_government: ''
      });
      
      onBusinessAdded();
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding business:', error);
      toast.error('Failed to submit business listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get available LGAs for selected state
  const availableLGAs = formData.state ? mockLGAs[formData.state] || [] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-omoduduwa-terracotta">Add Business to Marketplace</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="business_name" className="text-gray-900">Business Name *</Label>
            <Input
              id="business_name"
              value={formData.business_name}
              onChange={(e) => handleInputChange('business_name', e.target.value)}
              placeholder="Your business name"
              className="bg-white text-gray-900 placeholder:text-gray-500"
              required
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-900">Business Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe your business and what you offer..."
              className="bg-white text-gray-900 placeholder:text-gray-500"
              rows={3}
              required
            />
          </div>

          <div>
            <Label htmlFor="business_category" className="text-gray-900">Business Category (Optional)</Label>
            <Select value={formData.business_category} onValueChange={(value) => handleInputChange('business_category', value)}>
              <SelectTrigger className="bg-white text-gray-900">
                <SelectValue placeholder="Select category (optional)" />
              </SelectTrigger>
              <SelectContent>
                {businessCategories.map((category) => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="contact_phone" className="text-gray-900">Contact Phone *</Label>
            <Input
              id="contact_phone"
              value={formData.contact_phone}
              onChange={(e) => handleInputChange('contact_phone', e.target.value)}
              placeholder="+234 800 000 0000"
              className="bg-white text-gray-900 placeholder:text-gray-500"
              required
            />
          </div>

          <div>
            <Label htmlFor="website_url" className="text-gray-900">Website (Optional)</Label>
            <Input
              id="website_url"
              value={formData.website_url}
              onChange={(e) => handleInputChange('website_url', e.target.value)}
              placeholder="example.com or https://example.com"
              className="bg-white text-gray-900 placeholder:text-gray-500"
            />
            <p className="text-xs text-gray-600 mt-1">Optional - Leave blank if you don't have a website</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="state" className="text-gray-900">State *</Label>
              <Select value={formData.state} onValueChange={(value) => handleInputChange('state', value)} required>
                <SelectTrigger className="bg-white text-gray-900">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {nigerianStates.map((state) => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="local_government" className="text-gray-900">Local Government *</Label>
              <Select 
                value={formData.local_government} 
                onValueChange={(value) => handleInputChange('local_government', value)} 
                required
                disabled={!formData.state}
              >
                <SelectTrigger className="bg-white text-gray-900">
                  <SelectValue placeholder={formData.state ? "Select Local Government" : "Select state first"} />
                </SelectTrigger>
                <SelectContent>
                  {availableLGAs.map((lga) => (
                    <SelectItem key={lga} value={lga}>{lga}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90"
            >
              {loading ? 'Submitting...' : 'Submit Business'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MarketplaceForm;
